import {
  authFactory,
  createModularAuthAdapter,
  createModularFirestoreAdapter,
  createWebLocalStorageAdapter,
  dataSourceRegistry,
  FirestoreDataSource,
  LocalStorageDataSource,
} from '@rammetec/data-access'
import { registerDescriptors } from '@vectorhub/domain'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore'
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth'
import { initializeApp, deleteApp } from 'firebase/app'
import { firebaseApp, firebaseConfig, firestoreDb } from './firebase'

let bootstrapped = false

/**
 * Registers data-access providers with Firebase Web SDK and the VH entity descriptors.
 * Called once at app boot (main.tsx). Must run before any repository is built.
 *
 * No registerManifests() — VH domain has no manifest registry (D0009/Phase 3).
 */
export function bootstrapDataAccess(): void {
  if (bootstrapped) return
  bootstrapped = true

  const firestoreAdapter = createModularFirestoreAdapter({
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
  })

  dataSourceRegistry.registerFirestore((path, tableName) => {
    return new FirestoreDataSource(path, tableName, firestoreDb, firestoreAdapter)
  })

  const localStorageAdapter = createWebLocalStorageAdapter()
  dataSourceRegistry.registerLocalStorage((path, tableName) => {
    return new LocalStorageDataSource(path, tableName, localStorageAdapter)
  })

  const authAdapter = createModularAuthAdapter({
    getAuth: () => getAuth(firebaseApp),
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    onAuthStateChanged,
    persistence: {
      setPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    },
    secondaryApp: {
      initializeApp,
      deleteApp,
      getAuth,
      config: firebaseConfig,
    },
  })

  authFactory.configure({
    adapter: authAdapter,
    firebaseConfig,
  })

  registerDescriptors()
}
